// Mujo cart drawer: styling that matches the mobile menu (same header height and faint
// full-width dividers, the site's sans font for all text, 3:4 photos like the product cards,
// a soft natural tone on phones), and slimmer express-payment buttons
// (shown side by side via snippets/cart-summary.liquid). "Pairs well with" was removed for now.
// Loaded from snippets/cart-drawer.liquid.

const STYLE_ID = 'mujo-cart-css';
const LINE = 'color-mix(in srgb, var(--color-foreground) 8%, transparent)';
const SOFT = 'color-mix(in srgb, var(--color-foreground) 68%, transparent)';
// Everything in the cart uses the site's sans font (the heading/subheading font)
const SANS = 'var(--font-subheading--family, var(--font-heading--family))';
const BODY = SANS;

const CSS = `
/* One font throughout the cart: the site's sans (labels, prices, buttons, headings) */
.cart-drawer__dialog,
.cart-drawer__dialog *:not(svg):not(path) { font-family: ${BODY} !important; }
/* Header: same height and divider as the site header / mobile menu bar */
.cart-drawer__dialog .cart-drawer__header {
  min-height: 61px;
  padding-block: 0;
  border-block-end: 1px solid ${LINE};
}
.cart-drawer__dialog .cart-drawer__heading {
  font-family: ${BODY};
  font-size: 17px;
  font-weight: 400;
  letter-spacing: 0;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}
.cart-drawer__dialog .cart-drawer__heading .cart-bubble {
  display: inline-flex !important;
  align-items: baseline;
  background: none !important;
  color: ${SOFT};
  font-size: 14px;
  padding: 0;
  min-width: 0;
  width: auto;
  height: auto;
  aspect-ratio: auto !important;
}
.cart-drawer__dialog .cart-drawer__heading .cart-bubble__background { display: none; }
.cart-drawer__dialog .cart-drawer__heading .cart-bubble__text { position: static; display: inline; font-size: 14px; }
.cart-drawer__dialog .cart-drawer__heading .cart-bubble::before { content: '('; }
.cart-drawer__dialog .cart-drawer__heading .cart-bubble::after { content: ')'; }
.cart-drawer__dialog .cart-drawer__heading .cart-bubble * { background: none !important; color: inherit !important; }

/* Items: faint full-width lines between them, like the menu rows */
.cart-drawer__dialog .cart-drawer__items { padding-block-start: 0; }
.cart-drawer__dialog .cart-items__table-row {
  margin: 0 -20px 0 -16px;
  padding: 16px 20px 16px 16px;
  border-block-end: 1px solid ${LINE};
  grid-template-columns: 60px minmax(0, 1fr) auto;
  column-gap: 14px;
}
/* Photos 3:4, the same as the product cards. The image is laid over a fixed 3:4 box so it
   fills it the same way in every browser (Safari can mis-size images in a flex box). */
.cart-drawer__dialog .cart-items__media-container {
  position: relative;
  display: block;
  width: 60px;
  aspect-ratio: 3 / 4 !important;
  overflow: hidden;
  background: ${LINE};
}
.cart-drawer__dialog .cart-items__media-image {
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  aspect-ratio: auto;
  object-fit: cover;
}

/* Streamlined details: maker, name, then one quiet line (option · stock) */
.cart-drawer__dialog .cart-items__details,
.cart-drawer__dialog .cart-items__details * { font-family: ${BODY}; }
.cart-drawer__dialog .cart-items__details { color: ${SOFT}; }
.cart-drawer__dialog .cart-items__details > * + * { margin-block-start: 2px; }
.cart-drawer__dialog .cart-items__maker { font-size: 11px; letter-spacing: 0.02em; }
.cart-drawer__dialog .cart-items__title { font-size: 13.5px; line-height: 1.3; }
.cart-drawer__dialog .cart-items__variants,
.cart-drawer__dialog .cart-items__variants * ,
.cart-drawer__dialog .cart-items__stock { font-size: 12px; }
.cart-drawer__dialog .cart-items__variants,
.cart-drawer__dialog .cart-items__stock { display: inline; }
.cart-drawer__dialog .cart-items__variants ~ .cart-items__stock::before { content: ' · '; }
.cart-drawer__dialog .cart-items__variant,
.cart-drawer__dialog .cart-items__variants dd { display: inline; }
.cart-drawer__dialog .cart-items__table-row { --cart-item-price-width: 3.5rem; }
/* The unit price repeats the line price on the right, so it's hidden */
.cart-drawer__dialog .cart-items__details > div:not([class]) { display: none; }
.cart-drawer__dialog .cart-items__price {
  font-family: ${SANS};
  font-size: 13px;
  color: var(--color-foreground);
}

/* Quantity and remove sit right-aligned under the price, leaving the name its full width */
.cart-drawer__dialog .cart-items__table-row {
  grid-template-areas:
    'media details price'
    'media quantity quantity'
    'media error error';
  row-gap: 0;
}
.cart-drawer__dialog .cart-items__price { align-self: start; }
.cart-drawer__dialog .cart-items__quantity { justify-self: end; width: auto; margin-block-start: 6px; gap: 4px; }
.cart-drawer__dialog .cart-items__quantity .quantity-selector {
  height: 26px;
  min-height: 26px;
  width: 78px;
  background: transparent;
  border: 1px solid color-mix(in srgb, var(--color-foreground) 14%, transparent);
  border-radius: 999px;
  --quantity-selector-width: 78px;
  flex: 0 0 78px;
}
.cart-drawer__dialog .cart-items__quantity .quantity-selector button { width: 24px; min-width: 24px; height: 24px; padding: 0; }
.cart-drawer__dialog .cart-items__quantity .quantity-selector button svg { width: 9px; height: 9px; }
.cart-drawer__dialog .cart-items__quantity .quantity-selector input {
  flex: 1 1 auto;
  width: 26px;
  min-width: 20px;
  height: 24px;
  padding: 0;
  text-align: center;
  font-family: ${SANS};
  font-size: 12px;
  background: transparent;
}
.cart-drawer__dialog .cart-items__remove { width: 26px; height: 26px; color: ${SOFT}; margin-inline-end: -6px; } /* icon lines up with the price's right edge */
.cart-drawer__dialog .cart-items__remove svg { width: 14px; height: 14px; }

/* Summary: compact, so the cart items keep most of the screen */
.cart-drawer__dialog .cart-drawer__summary {
  gap: 12px;
  padding-block: 12px 16px;
  border-block-start: 1px solid ${LINE};
}
.cart-drawer__dialog .free-shipping { padding-block: 2px 0; }
.cart-drawer__dialog .cart__summary-totals { gap: 12px; }
.cart-drawer__dialog .cart__total-container { row-gap: 2px; }
.cart-drawer__dialog .tax-note,
.cart-drawer__dialog .tax-note * { font-size: 11px; }

/* Gift note and discount side by side; whichever is opened takes the full width */
.cart-drawer__dialog .cart-actions {
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0 24px;
  padding-block: 0;
  margin-block-start: 0;
}
.cart-drawer__dialog .cart-actions > cart-note,
.cart-drawer__dialog .cart-actions > cart-discount-component { flex: 1 1 0; min-width: 0; }
.cart-drawer__dialog .cart-actions > :has(details[open]) { flex-basis: 100%; }
.cart-drawer__dialog .cart-actions__divider { display: none; }
.cart-drawer__dialog .cart-actions summary { min-height: 44px; padding-block: 0; }
.cart-drawer__dialog .cart-actions summary span:first-child,
.cart-drawer__dialog .cart-note__label { font-size: 14px; }

/* Summary labels and totals */
.cart-drawer__dialog .cart__total-label,
.cart-drawer__dialog .cart__original-total-label,
.cart-drawer__dialog .cart-note__label,
.cart-drawer__dialog .cart-discount__label,
.cart-drawer__dialog .cart-discount summary span:first-child,
.cart-drawer__dialog .free-shipping__text,
.cart-drawer__dialog .tax-note {
  font-family: ${BODY};
}
.cart-drawer__dialog .cart__total-value,
.cart-drawer__dialog .cart__original-total-value,
.cart-drawer__dialog .free-shipping__text strong { font-family: ${SANS}; }
.cart-drawer__dialog .cart__total-label { font-size: 15px; }
.cart-drawer__dialog .cart__total-value { font-size: 20px; }
.cart-drawer__dialog .cart-actions { border-color: ${LINE}; }
.cart-drawer__dialog .cart-actions__divider { border-color: ${LINE}; }
.cart-drawer__dialog .tax-note { color: ${SOFT}; }

/* Express payment buttons: slimmer, side by side, under a small "or" line */
.cart-drawer__dialog shopify-accelerated-checkout-cart {
  --shopify-accelerated-checkout-button-block-size: 38px;
  --shopify-accelerated-checkout-row-gap: 8px;
}
.cart-drawer__dialog .additional-checkout-buttons::before {
  content: 'or check out with';
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: ${BODY};
  font-size: 12px;
  color: ${SOFT};
  margin-block-end: 6px;
}
.cart-drawer__dialog .cart__ctas { gap: 8px; }
.cart-drawer__dialog .cart__ctas .cart__checkout-button { height: 48px; }

/* Empty cart: collection squares */
.cart-drawer--empty .cart-drawer__content { justify-content: flex-start; padding-block-start: 24px; overflow-y: auto; }
.cart-empty-collections { width: 100%; box-sizing: border-box; text-align: start; margin-block-start: 28px; padding: 0 20px 24px 16px; }
.cart-empty-collections__title { font-family: ${BODY}; font-size: 14px; color: ${SOFT}; margin: 0 0 12px; }
.cart-empty-collections__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin: 0; padding: 0; list-style: none; }
.cart-empty-collections__card { display: block; color: var(--color-foreground); text-decoration: none; }
.cart-empty-collections__media { display: block; aspect-ratio: 3 / 4; overflow: hidden; background: ${LINE}; }
.cart-empty-collections__media img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.4s ease; }
.cart-empty-collections__card:hover img { transform: scale(1.03); }
.cart-empty-collections__name { display: block; font-size: 15px; margin-block-start: 6px; }

/* Phones: a soft natural tone, like the mobile menu sections */
@media screen and (max-width: 989px) {
  .cart-drawer__dialog {
    --mujo-cart-tone: color-mix(in srgb, #e8e1d5 40%, var(--color-background));
    background-color: var(--mujo-cart-tone);
  }
  .cart-drawer__dialog .cart-drawer__header,
  .cart-drawer__dialog .cart-drawer__summary { background-color: var(--mujo-cart-tone); }
  .cart-drawer__dialog .cart-drawer__content { background-color: transparent; }
}
`;

const ensureStyles = () => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.append(style);
};

if (!window.__mujoCart) {
  window.__mujoCart = true;
  ensureStyles();
}